'use client';

import React from 'react';
import { Container, CustomImage } from 'components/shared';
import CustomLink from 'components/shared/CustomLink';
import useCluePricing from 'hooks/pricing/useCluePricing';
import { PrimaryCTA as PrimaryCTAButton } from 'components/shared/CTAComponent';
import { UHClueTitle } from './typography';
import styles from './GetStarted.module.css';

const StepBadge = ({
  label,
  color = '#ffffff',
}: {
  label: string | number;
  color?: string;
}) => {
  const maskId = `badge-mask-${label}`;
  return (
    <div className={styles.stepBadge} aria-hidden="true">
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
        <defs>
          <mask
            id={maskId}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="32"
            height="32"
          >
            <rect x="0" y="0" width="32" height="32" rx="8" fill="white" />
            <text
              x="16"
              y="16"
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="14"
              fontWeight="600"
              fontFamily="inherit"
              fill="black"
            >
              {label}
            </text>
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="32"
          height="32"
          rx="8"
          fill={color}
          mask={`url(#${maskId})`}
        />
      </svg>
    </div>
  );
};

type Step = {
  title: string;
  subtitle: string;
  img: string;
  badgeColor?: string;
};

const GetStarted = () => {
  const {
    ringPriceDisplay,
    clueDiscountedDisplay,
    cluePriceDisplay,
    bundlePriceDisplay,
  } = useCluePricing();

  const steps: Step[] = [
    {
      title: 'Buy the bundle',
      subtitle: `Get Ultrahuman Ring AIR for ${ringPriceDisplay}, and Clue Plus for only ${clueDiscountedDisplay} (worth ${cluePriceDisplay}).`,
      img: 'web_v2/uh-x-clue/get-started/clue-plus.png',
      badgeColor: '#FFFFFF',
    },
    {
      title: 'Activate your subscription',
      subtitle: 'Use the unique link from your email to activate Clue Plus.',
      img: 'web_v2/uh-x-clue/get-started/clue-sign-in.png',
      badgeColor: '#F6F6F6',
    },
    {
      title: 'Download Clue',
      subtitle: 'Log in and your Clue Plus will be synced.',
      img: 'web_v2/uh-x-clue/get-started/integrate.png',
      badgeColor: '#FFFFFF',
    },
    {
      title: 'Sync with Ultrahuman',
      subtitle:
        'Connect Clue via the Ultrahuman app to unlock deeper cycle, sleep, and recovery insights.',
      img: 'web_v2/uh-x-clue/get-started/sync.png',
      badgeColor: '#FFFFFF',
    },
  ];

  return (
    <section
      className={styles.section}
      role="region"
      aria-labelledby="getstarted-heading"
    >
      <Container>
        <UHClueTitle className={styles.title} id="getstarted-heading">
          How to get started
        </UHClueTitle>
        <div className={styles.grid}>
          {steps.map((step, idx) => (
            <div className={styles.card} key={step.title}>
              <div className={styles.imageWrap}>
                <StepBadge label={idx + 1} color={step.badgeColor} />
                <div className={styles.insetBorder} />
                <CustomImage
                  className={styles.image}
                  src={step.img}
                  alt={step.title}
                  fill
                />
              </div>
              <div className={styles.cardContent}>
                <h3 className={styles.cardTitle}>{step.title}</h3>
                <p
                  className={styles.cardSubtitle}
                  dangerouslySetInnerHTML={{ __html: step.subtitle }}
                />
              </div>
            </div>
          ))}
        </div>
        <div className={styles.ctaRow}>
          <CustomLink
            href={{ pathname: '/ring/buy', query: { flow: 'clue' } }}
            aria-label={`Buy Ultrahuman Ring AIR and Clue Plus bundle for ${bundlePriceDisplay}`}
          >
            <PrimaryCTAButton className={styles.primaryCta} color="black">
              Buy bundle for {bundlePriceDisplay}
            </PrimaryCTAButton>
          </CustomLink>
        </div>
      </Container>
    </section>
  );
};

export default GetStarted;
